import { RefObject, useContext, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router'
import { isEqual } from 'lodash'

import { AppContext } from '../../providers/app-provider'
import Api from '../../utils/api'
import { GLOBAL_API } from '../../utils/constants'

import formatApiData from './format-api-data'

export const tabMatrix = {
  accounts: 0,
  people: 1,
  devices: 2,
  orders: 3,
} as { [key in SearchTabsType]: number }

export const tabMatrixByIndex = Object.fromEntries(Object.entries(tabMatrix).map(([key, value]) => [value, key as SearchTabsType]))

export const getCurrentFilterCount = ({ tab, searchFilters }: { tab: SearchTabsType; searchFilters: IFilterOptions | undefined }) => {
  let currentFilterCount = 0
  if (searchFilters) {
    if (tab === 'accounts') {
      currentFilterCount += currentFilterCount + searchFilters.folderTypes.length
      if (searchFilters.hideDeactivated) currentFilterCount += 1
      if (searchFilters.excludeOptOut) currentFilterCount += 1
      if (searchFilters.hideNonProduction) currentFilterCount += 1
    }
    if (tab === 'people') {
      currentFilterCount += currentFilterCount + searchFilters.roleNames.length
      if (searchFilters.hideDeactivated) currentFilterCount += 1
    }
    if (tab === 'devices') {
      currentFilterCount += currentFilterCount + searchFilters.properties.length
      if (searchFilters.hideDeactivated) currentFilterCount += 1
      if (searchFilters.hideNonProduction) currentFilterCount += 1
    }
    if (tab === 'orders') {
      currentFilterCount += currentFilterCount + searchFilters.statuses.length
    }
  }
  return currentFilterCount
}

export const fetchSearchResults = async (
  query: string,
  tab: SearchTabsType,
  searchFilters: IFilterOptions,
  roles: IRoleType[],
  accessToken: string,
  limit = 20,
  offset = 0,
  abortController?: RefObject<AbortController | undefined>
) => {
  let res
  const deviceProperties: ISearchResultDeviceApi['properties'] = searchFilters.properties.map((p) => {
    if (p === 'Ammonia') return 'ammonia'
    if (p === 'Gateway') return 'gateway'
    if (p === 'LoRa') return 'lora'
    if (p === 'Ting') return 'ting'
    if (p === 'Water Shutoff') return 'bvs'
    return 'sensor'
  })

  let queryString = ''

  switch (tab) {
    case 'accounts':
      if (searchFilters.folderTypes.length > 0) queryString = `${queryString}${searchFilters.folderTypes.map((ft) => `&filters.folderTypes=${ft}`).join('')}`
      if (searchFilters.hideDeactivated) queryString = `${queryString}&filters.accountStatuses=Active&filters.accountStatuses=Not Activated`
      res = await Api.get(
        `${GLOBAL_API}/api/v1/search/folders?query=${encodeURIComponent(query)}${queryString}&filters.excludeOptOut=${searchFilters.excludeOptOut}&limit=${limit}&offset=${offset}`,
        undefined,
        undefined,
        abortController
      )
      break
    case 'people':
      if (searchFilters.roleNames.length > 0)
        queryString = `${queryString}${searchFilters.roleNames
          .map((rn) => roles.find((r) => r.name === rn)?.id)
          .filter((r) => r)
          .map((r) => `&filters.roleIds=${r}`)
          .join('')}`
      if (searchFilters.hideDeactivated) queryString = `${queryString}&filters.hasPassword=${searchFilters.hideDeactivated}`
      res = await Api.get(
        `${GLOBAL_API}/api/v1/search/users?query=${encodeURIComponent(query)}${queryString}&limit=${limit}&offset=${offset}`,
        undefined,
        undefined,
        abortController
      )
      break
    case 'devices':
      if (deviceProperties.length > 0) queryString = `${queryString}${deviceProperties.map((p) => `&filters.properties=${p}`).join('')}`
      if (searchFilters.hideDeactivated) queryString = `${queryString}&filters.isActive=${searchFilters.hideDeactivated}`
      res = await Api.get(
        `${GLOBAL_API}/api/v1/search/nodes?query=${encodeURIComponent(query)}${queryString}&filters.physicalOnly=true&limit=${limit}&offset=${offset}`,
        undefined,
        undefined,
        abortController
      )
      break
    default:
      if (searchFilters.statuses.length > 0) queryString = `${queryString}${searchFilters.statuses.map((s) => `&filters.orderStatuses=${s}`).join('')}`
      res = await Api.get(
        `${GLOBAL_API}/api/v1/search/orders?query=${encodeURIComponent(query)}${queryString}&limit=${limit}&offset=${offset}`,
        undefined,
        undefined,
        abortController
      )
  }
  return res
}

export const useFetchResults = ({ tab, q, searchFilters }: { tab: SearchTabsType; q: string; searchFilters: IFilterOptions | undefined }) => {
  const limit = 20
  const prevQuery = useRef(q)
  const prevTab = useRef(tab)
  const prevSearchFilters = useRef(searchFilters)
  const requestIdRef = useRef(0)
  const abortRef = useRef<AbortController | undefined>(undefined)

  const { roles, tree } = useContext(AppContext)
  const auth = useAuth()

  const [firstLoad, setFirstLoad] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [searchResults, setSearchResults] = useState<
    ISearchResultAccountApi[] | ISearchResultPersonApi[] | ISearchResultDeviceApi[] | ISearchResultOrderApi[] | undefined
  >(undefined)
  const [offset, setOffset] = useState<number>(0)
  const [hasMore, setHasMore] = useState<boolean>(false)

  const formattedSearchResults: SearchResultsType | undefined = useMemo(
    () => (tree && searchResults && prevTab.current === tab ? formatApiData(tab, searchResults, tree) : undefined),
    [searchResults, tab, tree]
  )
  const memoizedRoles = useMemo(() => roles, [roles])

  const fetchData = async (newOffset: number) => {
    if (!auth.user?.access_token || !searchFilters) return

    // cancel any in-flight request to prevent results being overwritten by older responses
    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller

    // increment requestId to identify the most recent request
    requestIdRef.current += 1
    const myRequestId = requestIdRef.current

    setError(false)
    setIsLoading(true)

    try {
      const res = await fetchSearchResults(q, tab, searchFilters, memoizedRoles, auth.user.access_token, limit, newOffset, abortRef)

      // don't update state if the request was aborted or if a newer request was made
      if (controller.signal.aborted || myRequestId !== requestIdRef.current) return

      if (!res.ok) {
        setError(true)
        setHasMore(false)
        if (newOffset === 0) setSearchResults([])
        return
      }

      let tenants
      if (tab === 'accounts') tenants = (res.body as IGlobalSearchFolders).tenantFolders
      if (tab === 'people') tenants = (res.body as IGlobalSearchUsers).tenantUsers
      if (tab === 'devices') tenants = (res.body as IGlobalSearchNodes).tenantNodes
      if (tab === 'orders') tenants = (res.body as IGlobalSearchOrders).tenantOrders

      if (!tenants) {
        if (newOffset === 0) setSearchResults([])
        setHasMore(false)
        return
      }

      const entries = Object.entries(tenants)
      const apiHasMore = entries.some(([, t]) => t.hasMore)

      const allRecords = entries.flatMap(([tenantId, tenantData]) =>
        tenantData.rows.map((item: IGlobalSearchFolders | IGlobalSearchUsers | IGlobalSearchNodes | IGlobalSearchOrders) => ({
          ...item,
          tenantId: Number(tenantId),
        }))
      )

      if (newOffset === 0) setSearchResults(allRecords as typeof searchResults)
      else
        setSearchResults((prev) => {
          if (prev && allRecords.length > 0) {
            return [...prev, ...allRecords] as typeof prev
          }
          return allRecords as typeof prev
        })
      setHasMore(apiHasMore)
      if (newOffset) setOffset(newOffset)
      prevQuery.current = q
      prevTab.current = tab
      prevSearchFilters.current = searchFilters
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return
      setError(true)
      setHasMore(false)
      if (newOffset === 0) setSearchResults([])
    } finally {
      if (!controller.signal.aborted && requestIdRef.current === myRequestId) {
        setIsLoading(false)
        setFirstLoad(false)
      }
    }
  }

  const fetchFirstData = useEffectEvent(async () => {
    await fetchData(0)
  })

  const fetchMoreData = async () => {
    if (isLoading || !hasMore) return
    await fetchData(offset + limit)
  }

  // Fetch data the first time and if q, tab, or searchFilters change
  useEffect(() => {
    const inputsChanged = !isEqual(q, prevQuery.current) || tab !== prevTab.current || !isEqual(searchFilters, prevSearchFilters.current)

    if (searchResults === undefined || inputsChanged) {
      setFirstLoad(true)
      setIsLoading(true)
      setSearchResults(undefined)
      setOffset(0)
      setHasMore(false)
      fetchFirstData()
    }

    // abort on unmount
    return () => {
      if (abortRef.current) abortRef.current.abort()
    }
  }, [q, tab, searchFilters, searchResults])

  return { isLoading, error, firstLoad, searchResults: formattedSearchResults, hasMore, fetchData, fetchMoreData }
}

export const navigateSearchResults = (q: string, tab: SearchTabsType, newFilters: IFilterOptions, navigate: NavigateFunction) => {
  let newCheckboxes: string[] = []
  if (tab === 'accounts') newCheckboxes = newFilters.folderTypes
  if (tab === 'devices') newCheckboxes = newFilters.properties
  if (tab === 'people') newCheckboxes = newFilters.roleNames
  if (tab === 'orders') newCheckboxes = newFilters.statuses
  const navigateCheckboxes = newCheckboxes.map((x) => encodeURIComponent(x)).join(',')

  const newToggles = []
  if (newFilters.hideDeactivated) newToggles.push('hideDeactivated')
  if (newFilters.excludeOptOut) newToggles.push('excludeOptOut')
  if (newFilters.hideNonProduction) newToggles.push('hideNonProduction')
  const navigateToggles = newToggles.join(',')

  const newSearchParams = `q=${encodeURIComponent(q)}&tab=${tab}&filters=${navigateCheckboxes}&toggles=${navigateToggles}`
  navigate(`/search-results?${newSearchParams}`)
}

export const defaultSearchFilters = {
  folderTypes: [],
  roleNames: [],
  properties: [],
  statuses: [],
  hideDeactivated: false,
  excludeOptOut: false,
  hideNonProduction: false,
} as IFilterOptions

export const useSearchFilters = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const q = searchParams.get('q') ?? ''
  const tab: SearchTabsType =
    searchParams.get('tab') === 'accounts' ||
    searchParams.get('tab') === 'people' ||
    searchParams.get('tab') === 'devices' ||
    searchParams.get('tab') === 'orders'
      ? (searchParams.get('tab') as SearchTabsType)
      : 'accounts'
  const searchParamFilters = searchParams.get('filters') ?? ''
  const searchParamToggles = searchParams.get('toggles') ?? ''

  const [searchFilters, setSearchFilters] = useState<IFilterOptions | undefined>()

  useEffect(() => {
    const searchParamFiltersArr = searchParamFilters ? searchParamFilters.split(',') : []
    const toggles = searchParamToggles.split(',')
    setSearchFilters({
      folderTypes: tab === 'accounts' ? (searchParamFiltersArr as FolderType[]) : [],
      roleNames: tab === 'people' ? searchParamFiltersArr : [],
      properties: tab === 'devices' ? searchParamFiltersArr : [],
      statuses: tab === 'orders' ? searchParamFiltersArr : [],
      hideDeactivated: toggles.includes('hideDeactivated') && (tab === 'accounts' || tab === 'devices' || tab === 'people'),
      excludeOptOut: toggles.includes('excludeOptOut') && tab === 'accounts',
      hideNonProduction: toggles.includes('hideNonProduction') && (tab === 'accounts' || tab === 'devices'),
    })
  }, [searchParamFilters, searchParamToggles, tab])

  const selectFilters = (newFilters: IFilterOptions) => {
    navigateSearchResults(q, tab, newFilters, navigate)
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const newTab = (Object.keys(tabMatrix)[newValue] ?? 'accounts') as SearchTabsType
    navigateSearchResults(q, newTab, searchFilters ?? defaultSearchFilters, navigate)
  }

  return { searchFilters, selectFilters, handleTabChange, q, tab, searchParamFilters, searchParamToggles }
}
